import {
  alpha,
  Avatar,
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SideBarIcon from "assets/svg/SidebarIcon";
import HamburgerIcon from "assets/svg/HamburgerIcon";
import theme, { responsiveFontSize } from "themes/Theme";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { navigationData } from "./navigationData";
import { useSelector } from "react-redux";
import { RootState } from "reduxStore/store";

interface IProps {
  isMobile: boolean;
  toggleDrawer: () => void;
  open: boolean;
}

const HeaderComponent: React.FC<IProps> = ({
  toggleDrawer,
  open,
  isMobile,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const breadCrumbTitle = useMemo(() => {
    if (pathname.includes("/dashboard/transactions/")) {
      return (
        <>
          <Typography
            sx={styles.activeText}
            onClick={() => navigate("/dashboard/transactions")}
            noWrap
          >
            Transactions
          </Typography>
          <Typography sx={styles.defaultText} noWrap>
            /
          </Typography>
          <Typography sx={styles.defaultText} noWrap>
            Transaction Details
          </Typography>
        </>
      );
    }

    const title = navigationData.find((item) => item.path === pathname)?.label;
    return (
      <Typography sx={styles.defaultText} noWrap>
        {title}
      </Typography>
    );
  }, [pathname]);

  return (
    <Box sx={styles.container}>
      {/* LEFT: Hamburger + Breadcrumb */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <IconButton onClick={toggleDrawer} sx={{ p: 0.5, flexShrink: 0 }}>
          {isMobile ? (
            <HamburgerIcon
              width={20}
              height={20}
              color={theme.palette.primary.main}
            />
          ) : (
            <SideBarIcon width={20} height={20} />
          )}
        </IconButton>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          <Typography
            sx={styles.activeText}
            onClick={() => navigate("/dashboard")}
            noWrap
          >
            Dashboard
          </Typography>
          <Typography sx={styles.defaultText} noWrap>
            /
          </Typography>
          {breadCrumbTitle}
        </Stack>
      </Stack>

      {/* RIGHT: Avatar + Name */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexShrink: 0, minWidth: 0 }}
      >
        <Avatar
          sx={{
            width: { xs: 30, md: 42 },
            height: { xs: 30, md: 42 },
            borderRadius: "50%", // Ensure it's a circle
            bgcolor: "#fffede",
            border: "1px solid #ccc", // Added circle border
          }}
        >
          <Typography
            sx={{
              background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: responsiveFontSize(18),
              textTransform: "uppercase",
            }}
          >
            {user?.data?.personal_details?.firstName?.charAt(0)}
          </Typography>
          <Typography
            sx={{
              background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: responsiveFontSize(18),
              textTransform: "uppercase",
            }}
          >
            {user?.data?.personal_details?.lastName?.charAt(0)}
          </Typography>
        </Avatar>
        <Typography sx={styles.profileName} noWrap>
          {user?.data?.personal_details?.firstName}{" "}
          {user?.data?.personal_details?.lastName}
        </Typography>
      </Stack>
    </Box>
  );
};

export default HeaderComponent;

const styles = {
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.background.default,
    borderRadius: "2.5em",
    boxShadow:
      "rgba(0, 0, 0, 0.1) 0px 0px 3px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px",
    padding: "0.8em 1.2em",
    marginBottom: "1em",
    flexWrap: "wrap",
    gap: 1,
  },
  defaultText: {
    fontSize: {
      xs: responsiveFontSize(11),
      sm: responsiveFontSize(13),
      md: responsiveFontSize(15),
    },
    color: alpha(theme.palette.text.primary, 0.4),
    fontWeight: 400,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  activeText: {
    fontSize: {
      xs: responsiveFontSize(11),
      sm: responsiveFontSize(13),
      md: responsiveFontSize(15),
    },
    color: alpha(theme.palette.text.primary, 1),
    fontWeight: 500,
    "&:hover": {
      cursor: "pointer",
    },
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  profileName: {
    fontSize: {
      xs: responsiveFontSize(11),
      sm: responsiveFontSize(13),
      md: responsiveFontSize(15),
    },
    color: theme.palette.text.primary,
    fontWeight: 400,
    maxWidth: 120,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
};
